// src/pages/billing/BillingProductListPage.tsx

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useBillingProduct, useI18n } from '@react/hooks';
import {
  Button,
  Input,
  Card,
  CardContent,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  Badge,
  ErrorAlert,
} from '@react/components';

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try { return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(dateStr); }
}

export const BillingProductListPage = observer(() => {
  const store = useBillingProduct();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');

  useEffect(() => {
    store.ListBillingProducts({
      pagination: { cursor: searchParams.get('cursor') || '', limit: 20, direction: 'forward' },
      sort: [], date_range: {} as any, filters: {} as any,
    });
  }, [searchParams]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(search ? { search } : {});
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <nav className="text-sm mb-2">
            <Link to="/billing" className="text-muted-foreground hover:text-foreground">Billing</Link>
            <span className="mx-2 text-muted-foreground">/</span>
            <span className="text-foreground">Products</span>
          </nav>
          <h1 className="text-2xl font-bold text-foreground">Billing Products</h1>
        </div>
        <Link to="/billing/products/new"><Button>Create Product</Button></Link>
      </div>

      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSearch} className="flex gap-4">
            <Input placeholder={t('common.search')} value={search} onChange={(e) => setSearch(e.target.value)} fullWidth={false} className="flex-1" />
            <Button type="submit" variant="secondary">{t('common.search')}</Button>
          </form>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>Unit</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="text-right">{t('common.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8">{t('common.loading')}</TableCell></TableRow>
            ) : store.items.length === 0 ? (
              <TableEmpty colSpan={6} message="No products found" />
            ) : (
              store.items.map((item: any) => (
                <TableRow key={item.entity?.id || item.id || Math.random()}>
                  <TableCell>
                    <Link to={`/billing/products/${item.entity?.id || item.id}`} className="text-primary hover:underline">
                      {String(item.name ?? '')}
                    </Link>
                  </TableCell>
                  <TableCell>{String(item.description ?? '--')}</TableCell>
                  <TableCell>{String(item.unit_label || item.unit || '--')}</TableCell>
                  <TableCell>
                    {item.is_active ? <Badge variant="success" size="sm">Active</Badge> : <Badge variant="secondary" size="sm">Inactive</Badge>}
                  </TableCell>
                  <TableCell>{formatDate(item.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Link to={`/billing/products/${item.entity?.id || item.id}`}><Button variant="ghost" size="sm">View</Button></Link>
                      <Link to={`/billing/products/${item.entity?.id || item.id}/edit`}><Button variant="ghost" size="sm">{t('common.edit')}</Button></Link>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
});

export default BillingProductListPage;
